<template>
    <v-card elevation="0">
        <div v-if="fullCard" class="mb-5">
            <v-card-title class="text-h6 font-weight-light align-baseline">
                {{ $t('available_offers') }}
                <span class="ml-4 text-body-2 text-primary cursor-pointer" @click="expanded = !expanded">
                    {{ $t('filters') }}
                    <v-icon color="primary" size="small">
                        {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                </span>
            </v-card-title>
            <v-expand-transition v-model="expanded">
                <div v-if="expanded">
                    <cruise-page-filters
                        :offers="sortedFilteredOffers"
                        :dates-for-filter="datesForFilter"
                        @change="setFilters"
                        @on-date-filter-change="onDateFilterChange"
                    />
                </div>
            </v-expand-transition>
        </div>
        <v-expansion-panels v-if="groupOffersBy !== 'none'" v-model="panels" multiple variant="accordion">
            <v-expansion-panel v-for="(offers, groupIndex) in groupedOffers" :key="`group-${groupIndex}`">
                <v-expansion-panel-title
                    class="text-nobreak font-weight-bold"
                    :class="$breakpoint.smAndDown ? 'body-2' : 'subtitle-1'"
                    color="blue-grey-lighten-5"
                >
                    <div v-if="groupOffersBy === 'startDate'">
                        {{ $filters.dateFormat(offers[0][groupOffersBy]) }}
                    </div>
                    <div v-else>
                        {{ offers[0][groupOffersBy] }}
                    </div>
                    <div class="d-flex align-center justify-space-between font-weight-bold text-right pr-4">
                        <v-spacer />
                        <template v-if="minPriceOffer(offers).price && !hidePrice && !minPriceOffer.soldOut">
                            {{ $t('from') }} {{ $filters.price(minPriceOffer(offers).price) }}
                        </template>
                    </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                    <cruise-offer-card
                        v-for="(offer, offerIndex) in offers"
                        :key="offerIndex"
                        :offer="offer"
                        :full-card="fullCard"
                        :grouped-by="groupOffersBy"
                        :data-cy="`cruise-offer-card-${offerIndex}`"
                        @images="$emit('images', $event)"
                        @info="$emit('info', $event)"
                    >
                        <template v-slot:price>
                            <slot name="price" :offer="offer" :offer-key="offer.offerKey" />
                        </template>
                    </cruise-offer-card>
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, Watch} from 'vue-facing-decorator'
    import SortSelect from '@/components/search/SortSelect.vue'
    import GroupOffersSelect from '@/components/search/GroupOffersSelect.vue'
    import CruiseOfferCard from '@/components/cruises/info/CruiseOfferCard.vue'
    import CruisePageFilters from '@/components/search/filters/CruisePageFilters.vue'
    import {CruisesStoreMixin} from '@/mixins/CruisesStoreMixin.vue'
    import {minPriceOffer} from '~/utils/sort-helpers'

    @Component({
        components: {
            CruisePageFilters,
            CruiseOfferCard,
            SortSelect,
            GroupOffersSelect,
        },
    })
    export class CruiseGroupedOffers extends mixins(CruisesStoreMixin) {
        @Prop({default: false, type: Boolean}) fullCard
        @Prop({default: false, type: Boolean}) hidePrice
        @Prop({default: () => []}) sortedFilteredOffers
        @Prop() datesForFilter

        panels = [0]
        expanded = false

        @Watch('groupOffersBy')
        onGroupOffersByChange() {
            this.panels = [0]
        }

        minPriceOffer(offers) {
            return minPriceOffer({offers})
        }

        @Emit()
        setFilters(filter) {
            return filter
        }

        @Emit()
        onDateFilterChange(date) {
            return date
        }

        get searchResponse() {
            return this.productStore.searchResponse
        }

        get groupedOffers() {
            let offers = [...this.sortedFilteredOffers]

            return offers.reduce((groupedOffers, offer) => {
                let group = groupedOffers.find(group => group[0][this.groupOffersBy] === offer[this.groupOffersBy])
                if (!group) {
                    group = []
                    groupedOffers.push(group)
                }
                group.push(offer)
                return groupedOffers
            }, [])
        }

        get productStore() {
            return cruisesStore
        }

        get groupOffersBy() {
            return this.productStore.groupOffersBy
        }

        get sortItems() {
            return [
                {
                    text: this.$t('sort.price_asc'),
                    value: 'priceAsc',
                },
                {
                    text: this.$t('sort.price_desc'),
                    value: 'priceDesc',
                },
                {
                    text: this.$t('sort.date_asc'),
                    value: 'dateAsc',
                },
            ]
        }
    }

    export default CruiseGroupedOffers
</script>

<style scoped>
    :deep(.v-expansion-panel-content__wrap) {
        padding-left: 0;
        padding-right: 0;
    }
</style>
